// Shared guided-debugger shell: titlebar, orientation, case toolbar, trace
// rail, system panel, inspector, transport controls, and detail cards. Every
// guided course renders through here; only the `shell` config and the lab
// data differ.
import { useEffect, useState } from 'react';
import { guidedLink } from '../dslabs/ui/guidedLinks';

export function GuidedDebugger({ lab, shell, onTryInSandbox }) {
    const [scenarioIndex, setScenarioIndex] = useState(0);
    const [stepIndex, setStepIndex] = useState(0);
    const [playing, setPlaying] = useState(false);
    const scenario = lab.scenarios[scenarioIndex];
    const currentStep = scenario.steps[stepIndex];
    const atEnd = stepIndex === scenario.steps.length - 1;
    const ref = shell.ref(lab);

    useEffect(() => {
        setScenarioIndex(0);
        setStepIndex(0);
        setPlaying(false);
    }, [lab.id]);

    useEffect(() => {
        if (!playing) return undefined;
        if (atEnd) {
            setPlaying(false);
            return undefined;
        }
        const timer = window.setTimeout(() => setStepIndex((index) => index + 1), 1750);
        return () => window.clearTimeout(timer);
    }, [playing, atEnd, stepIndex]);

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.target.closest?.('button, a, [role="button"]')) return;
            if (event.key === 'ArrowRight') selectStep(Math.min(scenario.steps.length - 1, stepIndex + 1));
            if (event.key === 'ArrowLeft') selectStep(Math.max(0, stepIndex - 1));
            if (event.key === ' ') {
                event.preventDefault();
                if (atEnd) setStepIndex(0);
                setPlaying((value) => !value);
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    });

    const selectScenario = (index) => {
        setScenarioIndex(index);
        setStepIndex(0);
        setPlaying(false);
    };

    const selectStep = (index) => {
        setStepIndex(index);
        setPlaying(false);
    };

    return (
        <section className="debugger-shell" aria-label={`${ref} interactive debugger`}>
            <div className="debugger-titlebar">
                <div className="window-dots" aria-hidden="true"><i /><i /><i /></div>
                <span>{shell.title(lab)}</span><span className="debugger-mode">DETERMINISTIC TRACE</span>
            </div>
            <div className="paxos-orientation">
                {shell.orientation.map(([number, strong, rest]) => (
                    <div key={number}><span className="orientation-number">{number}</span><p><strong>{strong}</strong> {rest}</p></div>
                ))}
            </div>
            <div className="scenario-toolbar">
                <div className="scenario-tabs" role="tablist" aria-label={`${ref} traces`}>
                    {lab.scenarios.map((item, index) => (
                        <button
                            key={item.id}
                            type="button"
                            role="tab"
                            aria-selected={item.id === scenario.id}
                            className={item.id === scenario.id ? 'is-active' : ''}
                            onClick={() => selectScenario(index)}
                        >
                            <span>{item.eyebrow}</span>{item.label}
                        </button>
                    ))}
                </div>
                <p>{scenario.summary}</p>
                {onTryInSandbox && guidedLink(lab.id, scenario.id) && (
                    <button
                        type="button"
                        className="lab-try-sandbox"
                        onClick={() => onTryInSandbox(lab.id, scenario.id)}
                    >
                        Try this failure in the Sandbox →
                    </button>
                )}
            </div>
            <div className="debugger-grid">
                <div className="trace-rail">
                    <div className="panel-label"><span>Trace</span><span>Step {stepIndex + 1} / {scenario.steps.length}</span></div>
                    <div className="trace-list">
                        {scenario.steps.map((traceStep, index) => (
                            <button
                                key={traceStep.name}
                                type="button"
                                className={'trace-event' + (index === stepIndex ? ' is-current' : index < stepIndex ? ' is-past' : '')}
                                onClick={() => selectStep(index)}
                            >
                                <span className="trace-index">{String(index + 1).padStart(2, '0')}</span>
                                <span className="trace-copy"><strong>{traceStep.name}</strong><small>{traceStep.message}</small></span>
                                <span className="trace-arrow" aria-hidden="true">›</span>
                            </button>
                        ))}
                    </div>
                </div>
                <div className="network-panel">
                    <div className="panel-label"><span>{shell.panelLabel}</span><span>{scenario.eyebrow}</span></div>
                    <div className="active-message-strip"><span className="active-message-dot" aria-hidden="true" /><strong>{currentStep.message}</strong><span>{currentStep.from} → {currentStep.to}</span></div>
                    {shell.renderDiagram(lab, scenario, currentStep)}
                    <div className="event-readout" aria-live="polite">
                        <div className="event-type">Step</div>
                        <div><span>Step {stepIndex + 1} / {scenario.steps.length}</span><h2>{currentStep.name}</h2><code>{currentStep.message}</code><p>{currentStep.detail}</p></div>
                        <div className="quorum-readout"><span>{shell.stateLabel}</span><strong>{currentStep.state}</strong></div>
                    </div>
                </div>
                <div className="paxos-inspector">
                    <div className="inspector-heading">
                        <div><span>Inspector</span><h2>{scenario.label}</h2></div>
                        <span className="role-pill">safe</span>
                    </div>
                    <dl className="node-state-grid">
                        <div><dt>State</dt><dd>{currentStep.state}</dd></div>
                        <div><dt>Message</dt><dd>{currentStep.message}</dd></div>
                        <div><dt>Route</dt><dd>{currentStep.from} → {currentStep.to}</dd></div>
                    </dl>
                </div>
            </div>
            <div className="debugger-controls">
                <button type="button" onClick={() => selectStep(0)}>↺ <span>Reset</span></button>
                <button type="button" aria-label={`Previous ${ref} event`} disabled={stepIndex === 0} onClick={() => selectStep(Math.max(0, stepIndex - 1))}>←</button>
                <button className="play-button" type="button" onClick={() => { if (atEnd) setStepIndex(0); setPlaying((value) => !value); }}>{playing ? 'Pause' : 'Play trace'} <span>{playing ? 'Ⅱ' : '▶'}</span></button>
                <button type="button" aria-label={`Next ${ref} event`} disabled={atEnd} onClick={() => selectStep(Math.min(scenario.steps.length - 1, stepIndex + 1))}>→</button>
                <div className="step-progress"><span style={{ width: `${(stepIndex / (scenario.steps.length - 1)) * 100}%` }} /></div>
                <span className="keyboard-hint">← → step · space play</span>
            </div>
            <StepDetails step={currentStep} />
        </section>
    );
}

export function LabReference({ lab }) {
    return (
        <>
            <div className="lab-analysis-grid">
                <section>
                    <div className="analysis-section-label"><span>Durable protocol state</span><span>01</span></div>
                    <dl className="lab-state-list">
                        {lab.state.map(([owner, value]) => <div key={owner}><dt>{owner}</dt><dd>{value}</dd></div>)}
                    </dl>
                </section>
                <section>
                    <div className="analysis-section-label"><span>Failure analysis</span><span>02</span></div>
                    <dl className="lab-failure-list">
                        {lab.failures.map(([failure, response]) => <div key={failure}><dt>{failure}</dt><dd>{response}</dd></div>)}
                    </dl>
                </section>
            </div>

            <div className="lab-guarantee">
                <span>Resulting guarantee</span>
                <strong>{lab.guarantee}</strong>
                <div>{lab.symbols.map((symbol) => <code key={symbol}>{symbol}</code>)}</div>
            </div>
        </>
    );
}

function StepDetails({ step }) {
    return (
        <section className="step-details" aria-label="Current event details">
            <div className="step-detail-card message-envelope">
                <span className="detail-kicker">Message envelope</span>
                <h3>{step.message}</h3>
                <div className="message-route"><code>{step.from}</code><span>→</span><code>{step.to}</code></div>
            </div>
            <div className="step-detail-card">
                <span className="detail-kicker">State changed</span>
                <ul><li>{step.state}</li></ul>
            </div>
            <div className="step-detail-card">
                <span className="detail-kicker">Why it matters</span>
                <h3>{step.invariant}</h3><p>{step.detail}</p>
            </div>
        </section>
    );
}
